import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  MdArrowBack,
  MdAssignment,
  MdAssessment,
  MdCalendarMonth,
  MdCameraAlt,
  MdCameraFront,
  MdDescription,
  MdEdit,
  MdFileDownload,
  MdHistory,
  MdNotifications,
  MdPlayArrow,
  MdSearch,
} from "react-icons/md";
import Card from "components/card";
import {
  getAttendanceFilterButtonClass,
  getAttendanceStatusBadgeClass,
  getAttendanceStatusSelectClass,
  getSessionStatusTone,
  SESSION_PARTICIPANT_ROLE_BADGE_CLASS,
} from "constants/colors";
import { classMgmtService } from "services/admin/classMgmtService";
import { venueService } from "services/admin/venueService";
import { api } from "services/api";
import { teachingAttendanceService } from "services/userTeaching/teachingAttendanceService";
import { teachingSessionService } from "services/userTeaching/teachingSessionService";
import type {
  AttendanceItem,
  BulkManualAttendancePayload,
  FaceAttendanceAction,
  FaceAttendanceResult,
} from "services/userTeaching/types";
import type {
  ClassAuditEvent,
  ClassEnrollment,
  ClassItem,
  ClassSession,
  SessionParticipant,
  SessionReport,
  UserRole,
  UserListItem,
  VenueItem,
} from "types/domain";
import { useNotification } from "context/NotificationContext";
import { useLanguage, type TranslateParams } from "context/LanguageContext";
import StatusBadge from "components/ui/StatusBadge";
import NotifySessionModal from "components/NotifySessionModal";
import ReportSubmitForm from "views/admin/sessions/ReportSubmitForm";
import ReportView from "views/admin/sessions/ReportView";
import { reportService } from "services/admin/reportService";
import { authService } from "services/authService";
import { getCurrentBackendRole } from "utils/auth";
import { parseLocation } from "./utils/sessionHelpers";

type Translate = (key: string, params?: TranslateParams) => string;

type ParticipantRole = "TEACHER" | "TRAINER" | "STUDENT";

const PARTICIPANT_ROLE_BADGE_CLASS: Record<ParticipantRole, string> = {
  TEACHER: SESSION_PARTICIPANT_ROLE_BADGE_CLASS.manager,
  TRAINER: SESSION_PARTICIPANT_ROLE_BADGE_CLASS.trainer,
  STUDENT: SESSION_PARTICIPANT_ROLE_BADGE_CLASS.student,
};

const statusBadgeKey = (status: ClassSession["status"]) => {
  if (status === "ACTIVE") return "enum.sessionStatus.ACTIVE";
  if (status === "COMPLETED" || status === "CLOSED")
    return "sessionDetailPage.status.done";
  if (status === "CANCELLED") return "enum.sessionStatus.CANCELLED";
  return "enum.sessionStatus.SCHEDULED";
};

const toRad = (value: number) => (value * Math.PI) / 180;

const distanceInMeters = (
  pointA: { lat: number; lng: number },
  pointB: { lat: number; lng: number }
) => {
  const radius = 6371000;
  const dLat = toRad(pointB.lat - pointA.lat);
  const dLng = toRad(pointB.lng - pointA.lng);
  const lat1 = toRad(pointA.lat);
  const lat2 = toRad(pointB.lat);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return radius * c;
};

const ATTENDANCE_STATUS_KEYS = new Set([
  "unmarked",
  "checked_in",
  "checked_out",
  "absent",
  "excused_absence",
]);

const attendanceStatusLabel = (t: Translate, status?: string | null) => {
  if (!status) return t("sessionDetailPage.attendanceStatus.unmarked");
  const normalized = status.toLowerCase();
  return ATTENDANCE_STATUS_KEYS.has(normalized)
    ? t(`sessionDetailPage.attendanceStatus.${normalized}`)
    : status;
};

const formatAttendanceTime = (
  iso: string | null | undefined,
  locale: string
): string | null => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const attendanceStatusLabelWithTime = (
  t: Translate,
  locale: string,
  status?: string | null,
  checkedInAt?: string | null,
  checkedOutAt?: string | null
) => {
  const base = attendanceStatusLabel(t, status);
  if (!status) return base;
  const normalized = status.toLowerCase();
  if (normalized === "checked_in") {
    const time = formatAttendanceTime(checkedInAt, locale);
    return time ? t("sessionDetailPage.attendance.inAt", { time }) : base;
  }
  if (normalized === "checked_out") {
    const inTime = formatAttendanceTime(checkedInAt, locale);
    const outTime = formatAttendanceTime(checkedOutAt, locale);
    if (inTime && outTime)
      return t("sessionDetailPage.attendance.inOutAt", { inTime, outTime });
    if (outTime)
      return t("sessionDetailPage.attendance.outAt", { time: outTime });
    return base;
  }
  return base;
};

type AttendanceStatusFilter =
  | "ALL"
  | "unmarked"
  | "checked_in"
  | "checked_out"
  | "absent"
  | "excused_absence";

const ATTENDANCE_FILTER_OPTIONS: Array<{
  value: AttendanceStatusFilter;
  labelKey: string;
}> = [
  {
    value: "unmarked",
    labelKey: "sessionDetailPage.attendanceStatus.unmarked",
  },
  {
    value: "checked_in",
    labelKey: "sessionDetailPage.attendanceStatus.checked_in",
  },
  {
    value: "checked_out",
    labelKey: "sessionDetailPage.attendanceStatus.checked_out",
  },
  { value: "absent", labelKey: "sessionDetailPage.attendanceStatus.absent" },
  {
    value: "excused_absence",
    labelKey: "sessionDetailPage.attendanceStatus.excused_absence",
  },
];

const CHECKIN_AFTER_CHECKOUT_ERROR_KEY =
  "sessionDetailPage.attendance.checkinAfterCheckout";

const formatDob = (t: Translate, locale: string, value?: string | null) => {
  if (!value) return t("sessionDetailPage.dobMissing");
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return t("sessionDetailPage.dobMissing");
  return parsed.toLocaleDateString(locale);
};

const formatShortDateTime = (
  t: Translate,
  locale: string,
  iso: string | null
) => {
  if (!iso) return t("sessionDetailPage.unscheduled");
  return new Date(iso).toLocaleString(locale, {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatSessionSchedule = (
  t: Translate,
  locale: string,
  start?: string | null,
  end?: string | null
) => {
  if (!start && !end) {
    return {
      timeText: t("sessionDetailPage.unscheduled"),
      dateText: "",
    };
  }

  const startDate = start ? new Date(start) : null;
  const endDate = end ? new Date(end) : null;
  const sameDay =
    startDate && endDate
      ? startDate.toLocaleDateString(locale) ===
        endDate.toLocaleDateString(locale)
      : false;

  if (startDate && endDate && sameDay) {
    return {
      timeText: `${startDate.toLocaleTimeString(locale, {
        hour: "2-digit",
        minute: "2-digit",
      })} - ${endDate.toLocaleTimeString(locale, {
        hour: "2-digit",
        minute: "2-digit",
      })}`,
      dateText: startDate.toLocaleDateString(locale, {
        weekday: "short",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }),
    };
  }

  return {
    timeText: [
      startDate ? formatShortDateTime(t, locale, start) : null,
      endDate ? formatShortDateTime(t, locale, end) : null,
    ]
      .filter(Boolean)
      .join(" • "),
    dateText: "",
  };
};

const SESSION_ACTIONS = new Set([
  "SESSION_CREATED",
  "SESSION_UPDATED",
  "SESSION_CANCELLED",
  "SESSION_COMPLETED",
  "SESSION_ACTIVATED",
  "SESSION_PARTICIPANTS_SYNCED",
]);

const formatSessionActionLabel = (t: Translate, action: string) =>
  SESSION_ACTIONS.has(action)
    ? t(`sessionDetailPage.auditAction.${action}`)
    : action;

const SESSION_AUDIT_ROLES = new Set<string>([
  "SYSTEM_ADMIN",
  "MANAGER",
  "TRAINER",
  "TEACHER",
  "STUDENT",
]);

const formatSessionAuditRole = (t: Translate, role: UserRole | null) => {
  if (!role) return t("enum.role.unknown");
  return SESSION_AUDIT_ROLES.has(role) ? t(`enum.role.${role}`) : role;
};

const SESSION_AUDIT_ENTITY_KEY: Record<string, string> = {
  session: "sessionDetailPage.auditEntity.session",
  session_participant: "sessionDetailPage.auditEntity.session_participant",
  class: "sessionDetailPage.auditEntity.class",
};

const formatSessionAuditEntityType = (t: Translate, entityType: string) => {
  const key = SESSION_AUDIT_ENTITY_KEY[entityType.trim().toLowerCase()];
  return key ? t(key) : entityType;
};

const SESSION_CHANGE_FIELDS = new Set([
  "start_time",
  "end_time",
  "duration_minutes",
  "status",
  "title",
  "description",
  "venue_id",
  "location",
  "teacher_ids",
  "trainer_ids",
  "student_ids",
]);

const formatSessionFieldLabel = (t: Translate, field: string) =>
  SESSION_CHANGE_FIELDS.has(field)
    ? t(`sessionDetailPage.auditField.${field}`)
    : field;

const SESSION_STATUS_KEY_MAP: Record<string, string> = {
  SCHEDULED: "enum.sessionStatus.SCHEDULED",
  ACTIVE: "enum.sessionStatus.ACTIVE",
  COMPLETED: "sessionDetailPage.status.done",
  CLOSED: "sessionDetailPage.status.done",
  CANCELLED: "enum.sessionStatus.CANCELLED",
};

const isSessionDateLikeField = (field: string) => {
  const normalized = field.toLowerCase();
  return normalized.includes("time") || normalized.includes("date");
};

const formatSessionAuditFieldValue = (
  t: Translate,
  locale: string,
  field: string,
  value: unknown
): string => {
  if (value === null || value === undefined || value === "")
    return t("sessionDetailPage.auditValue.empty");

  if (typeof value === "string") {
    if (field === "status") {
      const key = SESSION_STATUS_KEY_MAP[value];
      return key ? t(key) : value;
    }

    if (isSessionDateLikeField(field)) {
      const parsed = new Date(value);
      if (!Number.isNaN(parsed.getTime())) {
        return parsed.toLocaleString(locale);
      }
    }

    return value;
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return "[]";
    return value
      .map((item) => formatSessionAuditFieldValue(t, locale, field, item))
      .join(", ");
  }

  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const getSessionChangedFieldRows = (
  t: Translate,
  locale: string,
  event: ClassAuditEvent
) => {
  const shouldHideField = (field: string) => {
    const normalized = field.trim().toLowerCase();
    return (
      normalized === "created_at" ||
      normalized === "updated_at" ||
      normalized === "id" ||
      normalized.endsWith("_id") ||
      normalized.endsWith("_ids")
    );
  };

  const changedKeys = Object.keys(event.changed_fields ?? {});
  const fallbackKeys = Array.from(
    new Set([
      ...Object.keys(event.before_state ?? {}),
      ...Object.keys(event.after_state ?? {}),
    ])
  );

  const keys = changedKeys.length > 0 ? changedKeys : fallbackKeys;
  const visibleKeys = keys.filter((key) => !shouldHideField(key));

  return visibleKeys.map((key) => {
    const beforeRaw = event.before_state?.[key];
    const changedRaw = event.changed_fields?.[key];
    const afterRaw = event.after_state?.[key] ?? changedRaw;

    return {
      key,
      before: formatSessionAuditFieldValue(t, locale, key, beforeRaw),
      after: formatSessionAuditFieldValue(t, locale, key, afterRaw),
    };
  });
};

const getInitials = (name?: string | null) =>
  (name ?? "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("") || "?";

const UserAvatar = ({
  user,
  size = "md",
}: {
  user?: UserListItem | null;
  size?: "xs" | "sm" | "md";
}) => {
  const sizeClass =
    size === "xs"
      ? "h-8 w-8 text-[11px]"
      : size === "sm"
      ? "h-10 w-10 text-xs"
      : "h-12 w-12 text-sm";

  if (user?.avatar_url) {
    return (
      <img
        src={user.avatar_url}
        alt={user.full_name}
        className={`${sizeClass} rounded-full object-cover`}
      />
    );
  }

  return (
    <div
      className={`${sizeClass} flex items-center justify-center rounded-full bg-brand-50 font-semibold text-brand-600 dark:bg-brand-900/20 dark:text-brand-300`}
    >
      {getInitials(user?.full_name)}
    </div>
  );
};

const CompactParticipantRow = ({
  user,
  fallbackLabel,
  attendanceStatus,
  checkedInAt,
  checkedOutAt,
  onCheck,
  checking,
}: {
  user?: UserListItem | null;
  fallbackLabel: string;
  attendanceStatus?: string | null;
  checkedInAt?: string | null;
  checkedOutAt?: string | null;
  onCheck?: () => void;
  checking?: boolean;
}) => {
  const { t, locale } = useLanguage();
  const normalized = (attendanceStatus ?? "").toLowerCase();
  const isCheckedOut = normalized === "checked_out";
  return (
    <div className="flex items-center justify-between gap-3 py-2.5">
      <div className="flex min-w-0 items-center gap-2.5">
        <UserAvatar user={user} size="xs" />
        <div className="min-w-0">
          <p className="truncate text-xs font-semibold text-ink">
            {user?.full_name ?? user?.email ?? fallbackLabel}
          </p>
          <p className="truncate text-[11px] text-ink-muted">
            {user?.email ?? t("sessionDetailPage.noEmail")}
          </p>
        </div>
      </div>
      <div className="flex flex-shrink-0 items-center gap-1.5">
        {(() => {
          const inTime = formatAttendanceTime(checkedInAt, locale);
          const outTime = formatAttendanceTime(checkedOutAt, locale);
          const buttonAction =
            !normalized ||
            normalized === "absent" ||
            normalized === "excused_absence"
              ? "in"
              : normalized === "checked_in"
              ? "out"
              : null;
          return (
            <>
              {inTime && (
                <span className="border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200 rounded-full border px-2 py-0.5 text-[10px] font-medium">
                  {t("sessionDetailPage.attendance.inAt", { time: inTime })}
                </span>
              )}
              {outTime && (
                <span className="border-sky-300 bg-sky-50 text-sky-700 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-200 rounded-full border px-2 py-0.5 text-[10px] font-medium">
                  {t("sessionDetailPage.attendance.outAt", { time: outTime })}
                </span>
              )}
              <span
                className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${getAttendanceStatusBadgeClass(
                  attendanceStatus
                )}`}
              >
                {attendanceStatusLabel(t, attendanceStatus)}
              </span>
              {onCheck && !isCheckedOut && buttonAction && (
                <button
                  type="button"
                  disabled={checking}
                  onClick={onCheck}
                  className={`ml-1 rounded-lg px-2 py-0.5 text-[10px] font-semibold text-white disabled:opacity-50 ${
                    buttonAction === "in"
                      ? "bg-emerald-500 hover:bg-emerald-600"
                      : "bg-sky-500 hover:bg-sky-600"
                  }`}
                >
                  {checking
                    ? "..."
                    : t(`sessionDetailPage.attendance.${buttonAction}`)}
                </button>
              )}
            </>
          );
        })()}
      </div>
    </div>
  );
};

interface CaptureModalProps {
  sessionId: string;
  classId: string;
  defaultAction: FaceAttendanceAction;
  participants: Array<{
    userId: string;
    fullName: string;
    email?: string | null;
    status?: string | null;
    role?: ParticipantRole;
    roleLabel?: string;
    checkedInAt?: string | null;
    checkedOutAt?: string | null;
  }>;
  allowOutsideCheckin: boolean;
  sessionLocation?: string | null;
  onClose: () => void;
  onSuccess: () => void;
  /** When true, hide manual tab and only allow face check-in/out (trainer self-service). */
  trainerOnly?: boolean;
}

const CaptureModal: React.FC<CaptureModalProps> = ({
  sessionId,
  classId,
  defaultAction,
  participants,
  allowOutsideCheckin,
  sessionLocation,
  onClose,
  onSuccess,
  trainerOnly = false,
}) => {
  const { addNotification } = useNotification();
  const { t, locale } = useLanguage();
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const streamRef = React.useRef<MediaStream | null>(null);
  // The range error is never shown inline; remembering its exact text lets the
  // render tell it apart from other errors without matching translated words.
  const rangeErrorRef = React.useRef<string | null>(null);

  const [captured, setCaptured] = React.useState<string | null>(null);
  const [checking, setChecking] = React.useState(false);
  const [confirming, setConfirming] = React.useState(false);
  const [result, setResult] = React.useState<FaceAttendanceResult | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [cameraOn, setCameraOn] = React.useState(false);
  const [geoLocation, setGeoLocation] = React.useState<{
    latitude: number;
    longitude: number;
  } | null>(null);
  const [geoReady, setGeoReady] = React.useState(false);
  const [participantSearchKeyword, setParticipantSearchKeyword] =
    React.useState("");
  const [attendanceStatusFilter, setAttendanceStatusFilter] =
    React.useState<AttendanceStatusFilter>("ALL");
  const [selectedAction, setSelectedAction] =
    React.useState<FaceAttendanceAction>(defaultAction);
  const cameraStartedRef = React.useRef(false);

  // Manual attendance mode
  const [attendanceMode, setAttendanceMode] = React.useState<"face" | "manual">(
    "face"
  );
  const [showManualConfirm, setShowManualConfirm] = React.useState(false);
  const [pendingStatuses, setPendingStatuses] = React.useState<
    Record<string, string>
  >({});
  const [savingManual, setSavingManual] = React.useState(false);

  React.useEffect(() => {
    setSelectedAction(defaultAction);
  }, [defaultAction]);

  const startCamera = React.useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
      setCameraOn(true);
    } catch {
      setError(t("sessionDetailPage.capture.cameraError"));
    }
  }, [t]);

  const stopCamera = React.useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setCameraOn(false);
  }, []);

  React.useEffect(() => {
    if (!navigator.geolocation) {
      setGeoLocation(null);
      setGeoReady(true);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setGeoLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
        setGeoReady(true);
      },
      () => {
        setGeoLocation(null);
        setGeoReady(true);
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 15000 }
    );
  }, []);

  React.useEffect(() => {
    if (!geoReady) return;

    const sessionPoint = parseLocation(sessionLocation);
    if (!allowOutsideCheckin && sessionPoint) {
      if (!geoLocation) {
        setError(t("sessionDetailPage.capture.locationRequired"));
        return;
      }

      const distance = distanceInMeters(
        { lat: geoLocation.latitude, lng: geoLocation.longitude },
        sessionPoint
      );
      if (distance > 300) {
        const rangeError = t("sessionDetailPage.capture.outOfRange", {
          distance: distance.toFixed(1),
        });
        rangeErrorRef.current = rangeError;
        setError(rangeError);
        return;
      }
    }

    setError(null);
    if (attendanceMode !== "face") return;

    if (!cameraStartedRef.current) {
      cameraStartedRef.current = true;
      void startCamera();
    }
  }, [
    allowOutsideCheckin,
    attendanceMode,
    geoReady,
    geoLocation,
    sessionLocation,
    startCamera,
    t,
  ]);

  React.useEffect(() => {
    if (attendanceMode === "manual") {
      cameraStartedRef.current = false;
      stopCamera();
      return;
    }

    if (!captured && geoReady) {
      cameraStartedRef.current = false;
    }
  }, [attendanceMode, captured, geoReady, stopCamera]);

  React.useEffect(() => {
    return () => {
      cameraStartedRef.current = false;
      stopCamera();
    };
  }, [stopCamera]);

  const handleSaveManual = async () => {
    const changedEntries = Object.entries(pendingStatuses);
    if (changedEntries.length === 0) return;

    const hasInvalidCheckinAfterCheckout = changedEntries.some(
      ([userId, newStatus]) => {
        const participant = participants.find((item) => item.userId === userId);
        return (
          newStatus === "checked_in" &&
          (participant?.status ?? "").toLowerCase() === "checked_out"
        );
      }
    );
    if (hasInvalidCheckinAfterCheckout) {
      setShowManualConfirm(false);
      setError(t(CHECKIN_AFTER_CHECKOUT_ERROR_KEY));
      addNotification(t(CHECKIN_AFTER_CHECKOUT_ERROR_KEY), "error");
      return;
    }

    setShowManualConfirm(false);
    setSavingManual(true);
    setError(null);
    try {
      const items: BulkManualAttendancePayload[] = changedEntries.map(
        ([userId, newStatus]) => ({
          user_id: userId,
          session_id: sessionId,
          status: newStatus as BulkManualAttendancePayload["status"],
          location: geoLocation
            ? `${geoLocation.latitude.toFixed(
                6
              )},${geoLocation.longitude.toFixed(6)}`
            : undefined,
        })
      );

      await teachingAttendanceService.bulkManualAttendance(
        items,
        "Điểm danh thủ công" // i18n-ignore: note stored by the backend
      );
      setPendingStatuses({});
      onSuccess();
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : t("sessionDetailPage.capture.saveFailed");
      setError(message);
    } finally {
      setSavingManual(false);
    }
  };

  const handleRetake = () => {
    setCaptured(null);
    setResult(null);
    setError(null);
    void startCamera();
  };

  const handleCapture = async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    canvas.getContext("2d")?.drawImage(video, 0, 0);
    const capturedImage = canvas.toDataURL("image/jpeg", 0.9);
    setCaptured(capturedImage);
    stopCamera();
    setResult(null);

    setChecking(true);
    setError(null);
    try {
      const checkResult = await teachingAttendanceService.faceCheckAttendance(
        sessionId,
        capturedImage,
        geoLocation ?? undefined,
        "check",
        classId
      );
      setResult(checkResult);

      if (!checkResult.matched) {
        addNotification(`✗ ${t("selfCheckin.noMatch")}`, "error");
      }
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : t("sessionDetailPage.attendance.failed");
      const displayMessage =
        message === "Load failed"
          ? t("sessionDetailPage.capture.serverUnreachable")
          : message;
      setError(displayMessage);
      addNotification(displayMessage, "error");
    } finally {
      setChecking(false);
    }
  };
  const handleConfirmAttendance = async () => {
    if (!result?.matched || !result.user_id) return;

    setConfirming(true);
    setError(null);

    try {
      await teachingAttendanceService.submitManualAttendance({
        user_id: result.user_id,
        session_id: sessionId,
        action: "check",
        face_check_id: result.face_check_id ?? undefined,
        location: geoLocation
          ? `${geoLocation.latitude.toFixed(6)},${geoLocation.longitude.toFixed(
              6
            )}`
          : undefined,
        notes: "Confirmed after face recognition",
        auto_enroll_session: Boolean(result.requires_session_enrollment),
      });

      addNotification(
        t(
          selectedAction === "checkout"
            ? "sessionDetailPage.capture.checkoutSuccess"
            : "sessionDetailPage.capture.checkinSuccess",
          { name: result.full_name ?? result.user_id }
        ),
        "success"
      );

      // Keep modal open for continuous attendance flow.
      setResult(null);
      setCaptured(null);
      setError(null);
      void startCamera();
      onSuccess();
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : t("sessionDetailPage.capture.confirmFailed");
      setError(message);
      addNotification(message, "error");
    } finally {
      setConfirming(false);
    }
  };

  const recognizedParticipant = result?.user_id
    ? participants.find((item) => item.userId === result.user_id)
    : null;
  const filteredParticipants = React.useMemo(() => {
    const keyword = participantSearchKeyword.trim().toLowerCase();
    return participants.filter((participant) => {
      const normalizedStatus = (participant.status ?? "").toLowerCase();
      const matchesStatus =
        attendanceStatusFilter === "ALL"
          ? true
          : attendanceStatusFilter === "unmarked"
          ? !normalizedStatus
          : normalizedStatus === attendanceStatusFilter;
      if (!matchesStatus) return false;

      if (!keyword) return true;
      const fullName = participant.fullName.toLowerCase();
      const email = (participant.email ?? "").toLowerCase();
      return fullName.includes(keyword) || email.includes(keyword);
    });
  }, [attendanceStatusFilter, participantSearchKeyword, participants]);
  const canConfirm = Boolean(
    result?.matched &&
      result.user_id &&
      (recognizedParticipant || result.requires_session_enrollment)
  );
  const sessionPoint = React.useMemo(
    () => parseLocation(sessionLocation),
    [sessionLocation]
  );
  const distanceFromSession = React.useMemo(() => {
    if (!sessionPoint || !geoLocation) return null;
    return distanceInMeters(
      { lat: geoLocation.latitude, lng: geoLocation.longitude },
      sessionPoint
    );
  }, [geoLocation, sessionPoint]);
  const isInRange =
    distanceFromSession == null ? null : distanceFromSession <= 300;
  const isRangeRestrictionError =
    error != null && error === rangeErrorRef.current;
  const selectedActionLabel = t(
    selectedAction === "checkout"
      ? "sessionDetailPage.capture.checkout"
      : "sessionDetailPage.capture.checkin"
  );
  // Split around the count so it can be highlighted inside a single translated sentence.
  const [confirmSaveBefore, confirmSaveAfter = ""] = t(
    "sessionDetailPage.capture.confirmSaveMessage"
  ).split("{count}");

  return (
    <div
      className="bg-black/50 fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="flex max-h-[92vh] w-full max-w-7xl flex-col overflow-hidden rounded-3xl bg-surface shadow-2xl">
        <div className="flex items-center justify-between border-b border-line px-5 py-4">
          <div>
            <p className="text-sm font-semibold text-ink">
              {t("sessionDetailPage.capture.title")}
            </p>
            <p className="text-xs text-ink-muted">
              {t("sessionDetailPage.capture.subtitle")}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-ink-subtle hover:text-gray-600 dark:hover:text-gray-200"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>

        <div className="overflow-y-auto p-5">
          {/* Always two-column layout — left = camera/actions, right = participants */}
          <div className="mb-4 grid gap-4 lg:grid-cols-[minmax(0,1.35fr)_minmax(360px,1fr)]">
            <div>
              <div className="mb-4 rounded-2xl border border-line bg-gray-50/80 p-4 dark:bg-white/5">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-semibold text-ink">
                    {t("sessionDetailPage.capture.chooseType")}
                  </p>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                  <span className="font-medium text-ink-muted">
                    {t("sessionDetailPage.capture.distance")}
                  </span>
                  {!sessionPoint ? (
                    <span className="text-ink-muted">
                      {t("sessionDetailPage.capture.noSessionCoords")}
                    </span>
                  ) : !geoReady ? (
                    <span className="text-amber-600 dark:text-amber-300">
                      {t("sessionDetailPage.capture.locating")}
                    </span>
                  ) : distanceFromSession == null ? (
                    <span className="text-red-600 dark:text-red-300">
                      {t("sessionDetailPage.capture.locationUnavailable")}
                    </span>
                  ) : (
                    <span
                      className={
                        isInRange
                          ? "text-emerald-600 dark:text-emerald-300"
                          : "text-amber-600 dark:text-amber-300"
                      }
                    >
                      {t(
                        isInRange
                          ? "sessionDetailPage.capture.distanceInRange"
                          : "sessionDetailPage.capture.distanceOutOfRange",
                        { distance: distanceFromSession.toFixed(1) }
                      )}
                    </span>
                  )}
                </div>
                <div
                  className={`mt-3 grid gap-2 ${
                    trainerOnly ? "grid-cols-2" : "grid-cols-3"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedAction("checkin");
                      setAttendanceMode("face");
                    }}
                    className={`rounded-xl border px-3 py-2 text-xs font-semibold transition-colors ${
                      attendanceMode === "face" && selectedAction === "checkin"
                        ? "border-brand-500 bg-brand-500 text-white"
                        : "border-line text-gray-600 hover:bg-surface-hover dark:text-gray-300"
                    }`}
                  >
                    {t("sessionDetailPage.capture.checkin")}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedAction("checkout");
                      setAttendanceMode("face");
                    }}
                    className={`rounded-xl border px-3 py-2 text-xs font-semibold transition-colors ${
                      attendanceMode === "face" && selectedAction === "checkout"
                        ? "border-brand-500 bg-brand-500 text-white"
                        : "border-line text-gray-600 hover:bg-surface-hover dark:text-gray-300"
                    }`}
                  >
                    {t("sessionDetailPage.capture.checkout")}
                  </button>
                  {!trainerOnly && (
                    <button
                      type="button"
                      onClick={() => {
                        setAttendanceMode("manual");
                        setCaptured(null);
                        setResult(null);
                        cameraStartedRef.current = false;
                        stopCamera();
                      }}
                      className={`rounded-xl border px-3 py-2 text-xs font-semibold transition-colors ${
                        attendanceMode === "manual"
                          ? "border-brand-500 bg-brand-500 text-white"
                          : "border-line text-gray-600 hover:bg-surface-hover dark:text-gray-300"
                      }`}
                    >
                      {t("sessionDetailPage.capture.manual")}
                    </button>
                  )}
                </div>
              </div>

              <div className="ui-aspect-4-3 bg-gray-950 rounded-2xl border border-line p-3">
                <div className="bg-black relative h-full overflow-hidden rounded-xl">
                  {attendanceMode === "face" && !captured ? (
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      className="h-full w-full object-cover"
                    />
                  ) : null}
                  {attendanceMode === "face" && captured ? (
                    <img
                      src={captured}
                      alt={t("sessionDetailPage.capturedImageAlt")}
                      className="h-full w-full object-cover"
                    />
                  ) : null}
                  {attendanceMode === "face" && captured && result ? (
                    <div className="bg-black/65 absolute bottom-2 left-2 max-w-[85%] rounded-lg px-3 py-2 text-xs text-white backdrop-blur-sm">
                      <p className="font-semibold">
                        {canConfirm
                          ? result.requires_session_enrollment
                            ? t("sessionDetailPage.capture.willAddToSession", {
                                name: result.full_name ?? result.user_id ?? "",
                              })
                            : `${
                                recognizedParticipant?.fullName ??
                                result.full_name ??
                                result.user_id
                              }`
                          : result.matched
                          ? t("sessionDetailPage.capture.notInList", {
                              name: result.full_name ?? result.user_id ?? "",
                            })
                          : t("selfCheckin.noMatch")}
                      </p>
                      {result.similarity_score != null ? (
                        <p className="mt-1 text-[11px] text-gray-200">
                          {t("sessionDetailPage.capture.similarity", {
                            score: (result.similarity_score * 100).toFixed(1),
                          })}
                        </p>
                      ) : null}
                    </div>
                  ) : null}
                  {attendanceMode === "manual" ? (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <p className="text-xs text-ink-subtle">
                        {t("sessionDetailPage.capture.cameraOffManual")}
                      </p>
                    </div>
                  ) : null}
                  {attendanceMode === "face" && !cameraOn && !captured ? (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <p className="text-xs text-ink-subtle">
                        {t("sessionDetailPage.capture.cameraStarting")}
                      </p>
                    </div>
                  ) : null}
                </div>
              </div>
              <canvas ref={canvasRef} className="hidden" />

              <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
                {!captured ? (
                  <button
                    type="button"
                    disabled={
                      !cameraOn || checking || attendanceMode === "manual"
                    }
                    onClick={() => {
                      void handleCapture();
                    }}
                    className="rounded-xl bg-brand-500 px-4 py-2 text-xs font-semibold text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {checking
                      ? t("sessionDetailPage.capture.recognizing")
                      : t("selfCheckin.capture")}
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={handleRetake}
                      className="rounded-xl border border-line px-4 py-2 text-xs font-semibold text-gray-600 hover:bg-surface-hover dark:text-gray-300"
                    >
                      {t("selfCheckin.retake")}
                    </button>
                    <button
                      type="button"
                      disabled={!canConfirm || confirming || checking}
                      onClick={handleConfirmAttendance}
                      className="rounded-xl bg-navy-700 px-4 py-2 text-xs font-semibold text-white hover:bg-navy-800 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-white dark:text-navy-900 dark:hover:bg-gray-100"
                    >
                      {confirming
                        ? t("sessionDetailPage.capture.confirming")
                        : result?.requires_session_enrollment
                        ? t("sessionDetailPage.capture.confirmAndAdd", {
                            action: selectedActionLabel,
                          })
                        : t("sessionDetailPage.capture.confirmAction", {
                            action: selectedActionLabel,
                          })}
                    </button>
                  </>
                )}
              </div>

              {error && !isRangeRestrictionError ? (
                <div className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200">
                  {error}
                </div>
              ) : null}
            </div>

            <div className="rounded-2xl border border-line bg-gray-50/80 p-4 dark:bg-white/5">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-semibold text-ink">
                  {t("sessionDetailPage.capture.participants")}
                </p>
                <div className="flex flex-wrap items-center justify-end gap-2">
                  {attendanceMode === "manual" ? (
                    <>
                      {Object.keys(pendingStatuses).length > 0 && (
                        <span className="text-[11px] font-medium text-brand-600 dark:text-brand-300">
                          {t("sessionDetailPage.capture.pendingChanges", {
                            count: Object.keys(pendingStatuses).length,
                          })}
                        </span>
                      )}
                      <button
                        type="button"
                        disabled={
                          Object.keys(pendingStatuses).length === 0 ||
                          savingManual
                        }
                        onClick={() => setShowManualConfirm(true)}
                        className="rounded-lg bg-brand-500 px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {savingManual ? t("action.saving") : t("action.save")}
                      </button>
                      <button
                        type="button"
                        disabled={savingManual}
                        onClick={() => setPendingStatuses({})}
                        className="rounded-lg border border-line px-3 py-1.5 text-[11px] font-semibold text-gray-600 hover:bg-surface-hover dark:text-gray-300"
                      >
                        {t("action.cancel")}
                      </button>
                    </>
                  ) : null}
                </div>
              </div>
              <div className="mt-2 flex flex-nowrap items-center justify-start gap-2 overflow-x-auto pb-1">
                {ATTENDANCE_FILTER_OPTIONS.map((option) => {
                  const active = attendanceStatusFilter === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() =>
                        setAttendanceStatusFilter((prev) =>
                          prev === option.value ? "ALL" : option.value
                        )
                      }
                      className={`whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${getAttendanceFilterButtonClass(
                        option.value,
                        active
                      )}`}
                    >
                      {t(option.labelKey)}
                    </button>
                  );
                })}
                <span className="rounded-full bg-surface-muted px-2 py-0.5 text-[11px] font-medium text-gray-600 dark:text-gray-300">
                  {filteredParticipants.length}/{participants.length}
                </span>
              </div>
              <div className="mt-3">
                <input
                  type="text"
                  value={participantSearchKeyword}
                  onChange={(e) => setParticipantSearchKeyword(e.target.value)}
                  placeholder={t("sessionDetailPage.searchPlaceholder")}
                  className="w-full rounded-xl border border-line bg-white px-3 py-2 text-xs text-ink outline-none focus:border-brand-500 dark:bg-canvas/40"
                />
              </div>
              {attendanceMode === "manual" ? (
                /* ── Manual mode: editable status rows ── */
                <div className="mt-3 max-h-[580px] overflow-auto rounded-xl border border-line bg-white dark:bg-canvas/40">
                  {filteredParticipants.length === 0 ? (
                    <p className="px-3 py-3 text-xs text-ink-muted">
                      {t("sessionDetailPage.capture.noParticipantMatch")}
                    </p>
                  ) : (
                    filteredParticipants.map((participant) => {
                      const originalStatus = participant.status ?? "";
                      const pendingStatus = pendingStatuses[participant.userId];
                      const currentStatus = pendingStatus ?? originalStatus;
                      const isOriginallyCheckedOut =
                        originalStatus.toLowerCase() === "checked_out";
                      const hasPendingChange =
                        pendingStatus !== undefined &&
                        pendingStatus !== originalStatus;
                      return (
                        <div
                          key={participant.userId}
                          className={`flex items-center justify-between gap-3 border-b px-3 py-2.5 last:border-b-0 ${
                            hasPendingChange
                              ? "border-amber-300 bg-amber-100/60 dark:border-amber-500/40 dark:bg-amber-500/15"
                              : "border-line"
                          }`}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                              <p className="truncate text-xs font-semibold text-ink">
                                {participant.fullName}
                              </p>
                              {participant.roleLabel && (
                                <span
                                  className={`flex-shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                                    participant.role
                                      ? PARTICIPANT_ROLE_BADGE_CLASS[
                                          participant.role
                                        ]
                                      : SESSION_PARTICIPANT_ROLE_BADGE_CLASS.unknown
                                  }`}
                                >
                                  {participant.roleLabel}
                                </span>
                              )}
                            </div>
                            <p className="truncate text-[11px] text-ink-muted">
                              {participant.email ??
                                t("sessionDetailPage.noEmail")}
                            </p>
                          </div>
                          <select
                            value={currentStatus}
                            onChange={(e) => {
                              const newVal = e.target.value;
                              if (
                                isOriginallyCheckedOut &&
                                newVal === "checked_in"
                              ) {
                                setError(t(CHECKIN_AFTER_CHECKOUT_ERROR_KEY));
                                addNotification(
                                  t(CHECKIN_AFTER_CHECKOUT_ERROR_KEY),
                                  "error"
                                );
                                return;
                              }
                              setPendingStatuses((prev) => {
                                const next = { ...prev };
                                if (newVal === originalStatus) {
                                  delete next[participant.userId];
                                } else if (
                                  newVal !== "" ||
                                  originalStatus === ""
                                ) {
                                  next[participant.userId] = newVal;
                                }
                                return next;
                              });
                            }}
                            className={`flex-shrink-0 rounded-lg border px-2 py-1.5 text-xs outline-none focus:border-brand-500 dark:bg-surface dark:text-white ${getAttendanceStatusSelectClass( currentStatus || "unmarked"
                            )} ${
                              hasPendingChange
                                ? "ring-1 ring-amber-300 dark:ring-amber-500/40"
                                : ""
                            }`}
                          >
                            <option value="" disabled={originalStatus !== ""}>
                              {t("sessionDetailPage.attendanceStatus.unmarked")}
                            </option>
                            <option
                              value="checked_in"
                              disabled={isOriginallyCheckedOut}
                            >
                              {t("sessionDetailPage.manualStatus.checked_in")}
                            </option>
                            <option value="checked_out">
                              {t("sessionDetailPage.manualStatus.checked_out")}
                            </option>
                            <option value="absent">
                              {t("sessionDetailPage.attendanceStatus.absent")}
                            </option>
                            <option value="excused_absence">
                              {t(
                                "sessionDetailPage.attendanceStatus.excused_absence"
                              )}
                            </option>
                          </select>
                        </div>
                      );
                    })
                  )}
                </div>
              ) : (
                /* ── Face mode: read-only status badges ── */
                <div className="mt-3 max-h-[580px] overflow-auto rounded-xl border border-line bg-white p-2 dark:bg-canvas/40">
                  {filteredParticipants.length === 0 ? (
                    <p className="px-2 py-3 text-xs text-ink-muted">
                      {t("sessionDetailPage.noStudentMatch")}
                    </p>
                  ) : (
                    filteredParticipants.map((participant) => {
                      const isRecognized = Boolean(
                        result?.user_id && participant.userId === result.user_id
                      );
                      return (
                        <div
                          key={participant.userId}
                          className={`mb-1 flex items-center justify-between gap-2 rounded-lg border px-2.5 py-2 text-xs last:mb-0 ${
                            isRecognized
                              ? "border-brand-400 bg-brand-50 text-brand-700 dark:border-brand-500/60 dark:bg-brand-500/10 dark:text-brand-200"
                              : "border-line bg-white text-gray-600 dark:bg-surface/50 dark:text-gray-300"
                          }`}
                        >
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                              <p className="truncate font-semibold text-ink">
                                {participant.fullName}
                              </p>
                              {participant.roleLabel && (
                                <span
                                  className={`flex-shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                                    participant.role
                                      ? PARTICIPANT_ROLE_BADGE_CLASS[
                                          participant.role
                                        ]
                                      : SESSION_PARTICIPANT_ROLE_BADGE_CLASS.unknown
                                  }`}
                                >
                                  {participant.roleLabel}
                                </span>
                              )}
                            </div>
                            <p className="truncate text-[11px] text-ink-muted">
                              {participant.email ??
                                t("sessionDetailPage.noEmail")}
                            </p>
                          </div>
                          <span
                            className={`flex-shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${getAttendanceStatusBadgeClass(
                              participant.status
                            )}`}
                          >
                            {attendanceStatusLabelWithTime(
                              t,
                              locale,
                              participant.status,
                              participant.checkedInAt,
                              participant.checkedOutAt
                            )}
                          </span>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
              {error &&
              attendanceMode === "manual" &&
              !isRangeRestrictionError ? (
                <div className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200">
                  {error}
                </div>
              ) : null}
            </div>
          </div>

          {/* ── Manual attendance confirmation dialog ── */}
          {showManualConfirm && (
            <div className="bg-black/60 fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="w-full max-w-sm rounded-2xl bg-surface p-6 shadow-xl">
                <p className="text-sm font-semibold text-ink">
                  {t("sessionDetailPage.capture.confirmSaveTitle")}
                </p>
                <p className="mt-2 text-xs text-ink-muted">
                  {confirmSaveBefore}
                  <span className="font-semibold text-amber-600 dark:text-amber-300">
                    {Object.keys(pendingStatuses).length}
                  </span>
                  {confirmSaveAfter}
                </p>
                <div className="mt-5 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setShowManualConfirm(false)}
                    className="rounded-xl border border-line px-4 py-2 text-xs font-semibold text-gray-600 hover:bg-surface-hover dark:text-gray-300"
                  >
                    {t("action.cancel")}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      void handleSaveManual();
                    }}
                    className="rounded-xl bg-brand-500 px-4 py-2 text-xs font-semibold text-white hover:bg-brand-600"
                  >
                    {t("sessionDetailPage.capture.confirmSave")}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Self-service face check-in/out modal
// ---------------------------------------------------------------------------

interface SelfFaceCheckResponse {
  face_check_id: string | null;
  matched: boolean;
  user_id: string | null;
  full_name: string | null;
  similarity_score: number | null;
  message: string | null;
  attendance: {
    id: string;
    session_id: string;
    status: string | null;
    checked_in_at: string | null;
    checked_out_at: string | null;
    check_in_method: string | null;
  } | null;
}

interface SelfCheckModalProps {
  sessionId: string;
  onClose: () => void;
  onSuccess: () => void;
}

const SelfCheckModal: React.FC<SelfCheckModalProps> = ({
  sessionId,
  onClose,
  onSuccess,
}) => {
  const { addNotification } = useNotification();
  const { t, locale } = useLanguage();
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const streamRef = React.useRef<MediaStream | null>(null);

  const [cameraOn, setCameraOn] = React.useState(false);
  const [captured, setCaptured] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [result, setResult] = React.useState<SelfFaceCheckResponse | null>(
    null
  );
  const [errorMsg, setErrorMsg] = React.useState<string | null>(null);

  const startCamera = React.useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: 640, height: 480 },
      });
      streamRef.current = stream;
      setCameraOn(true);
      // Assign stream after a tick so the <video> element is in the DOM
      requestAnimationFrame(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      });
    } catch {
      setErrorMsg(t("selfCheckin.cameraError"));
    }
  }, [t]);

  const stopCamera = React.useCallback(() => {
    streamRef.current?.getTracks().forEach((tr) => tr.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraOn(false);
  }, []);

  React.useEffect(() => {
    void startCamera();
    return () => {
      streamRef.current?.getTracks().forEach((tr) => tr.stop());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCapture = React.useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    canvas.getContext("2d")?.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
    setCaptured(dataUrl);
    stopCamera();
  }, [stopCamera]);

  const handleRetake = React.useCallback(() => {
    setCaptured(null);
    setResult(null);
    setErrorMsg(null);
    void startCamera();
  }, [startCamera]);

  const handleSubmit = React.useCallback(async () => {
    if (!captured) return;
    setSubmitting(true);
    setResult(null);
    setErrorMsg(null);

    try {
      const resp = await fetch(captured);
      const blob = await resp.blob();

      let location: string | undefined;
      try {
        const pos = await new Promise<GeolocationPosition>((resolve, reject) =>
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            timeout: 5000,
          })
        );
        location = `${pos.coords.latitude},${pos.coords.longitude}`;
      } catch {
        // optional
      }

      const form = new FormData();
      form.append("file", blob, "capture.jpg");
      form.append("session_id", sessionId);
      if (location) form.append("location", location);

      const res = await api.upload<SelfFaceCheckResponse>(
        "/users/me/face/check",
        form
      );
      setResult(res);

      if (res.matched && res.attendance) {
        const status = res.attendance.status;
        if (status === "checked_in") {
          addNotification(t("selfCheckin.checkedIn"), "success");
        } else if (status === "checked_out") {
          addNotification(t("selfCheckin.checkedOut"), "success");
        } else {
          addNotification(t("selfCheckin.matchSuccess"), "success");
        }
        onSuccess();
      } else if (res.matched) {
        addNotification(t("selfCheckin.matchSuccess"), "success");
      } else {
        addNotification(t("selfCheckin.noMatch"), "warning");
      }
    } catch (err: any) {
      const msg = err.message || t("selfCheckin.error");
      setErrorMsg(msg);
      addNotification(msg, "error");
    } finally {
      setSubmitting(false);
    }
  }, [captured, sessionId, addNotification, t, onSuccess]);

  return (
    <div
      className="bg-black/50 fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-surface shadow-2xl">
        <div className="flex items-center justify-between border-b border-line px-5 py-4">
          <div>
            <p className="text-sm font-semibold text-ink">
              {t("selfCheckin.title")}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-ink-subtle hover:text-gray-600 dark:hover:text-gray-200"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>

        <div className="p-5">
          {/* Camera / captured image */}
          <div className="relative mb-4 overflow-hidden rounded-xl bg-surface-muted">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full rounded-xl"
              style={{ display: cameraOn && !captured ? "block" : "none" }}
            />
            {captured && (
              <img
                src={captured}
                alt={t("sessionDetailPage.capturedImageAlt")}
                className="w-full rounded-xl"
              />
            )}
            {!cameraOn && !captured && (
              <div className="flex h-52 items-center justify-center text-sm text-ink-subtle">
                {errorMsg || t("selfCheckin.cameraOff")}
              </div>
            )}
          </div>
          <canvas ref={canvasRef} className="hidden" />

          {/* Action buttons */}
          <div className="mb-4 flex gap-3">
            {cameraOn && !captured && (
              <button
                onClick={handleCapture}
                className="flex-1 rounded-lg bg-brand-500 px-4 py-2 text-sm text-white hover:bg-brand-600"
              >
                {t("selfCheckin.capture")}
              </button>
            )}
            {captured && !result && (
              <>
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="flex-1 rounded-lg bg-green-500 px-4 py-2 text-sm text-white hover:bg-green-600 disabled:opacity-50"
                >
                  {submitting
                    ? t("selfCheckin.submitting")
                    : t("selfCheckin.submit")}
                </button>
                <button
                  onClick={handleRetake}
                  disabled={submitting}
                  className="rounded-lg bg-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-400 dark:bg-surface-hover dark:text-white"
                >
                  {t("selfCheckin.retake")}
                </button>
              </>
            )}
            {result && (
              <button
                onClick={handleRetake}
                className="flex-1 rounded-lg bg-brand-500 px-4 py-2 text-sm text-white hover:bg-brand-600"
              >
                {t("selfCheckin.retake")}
              </button>
            )}
          </div>

          {/* Error */}
          {errorMsg && !cameraOn && captured && (
            <div className="mb-4 rounded-xl bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">
              {errorMsg}
            </div>
          )}

          {/* Result */}
          {result && (
            <div
              className={`rounded-xl p-4 ${
                result.matched
                  ? "bg-green-50 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                  : "bg-red-50 text-red-800 dark:bg-red-900/30 dark:text-red-300"
              }`}
            >
              <p className="font-semibold">
                {result.matched
                  ? t("selfCheckin.resultMatched")
                  : t("selfCheckin.resultNotMatched")}
              </p>
              {result.similarity_score != null && (
                <p className="text-sm">
                  {t("selfCheckin.score")}:{" "}
                  {(result.similarity_score * 100).toFixed(1)}%
                </p>
              )}
              {result.message && <p className="text-sm">{result.message}</p>}
              {result.attendance && (
                <div className="border-current/20 mt-2 border-t pt-2 text-sm">
                  <p className="font-medium">
                    {t("selfCheckin.attendanceStatus")}
                  </p>
                  <p>
                    {result.attendance.status === "checked_in"
                      ? t("selfCheckin.statusCheckedIn")
                      : result.attendance.status === "checked_out"
                      ? t("selfCheckin.statusCheckedOut")
                      : result.attendance.status}
                  </p>
                  {result.attendance.checked_in_at && (
                    <p>
                      {t("selfCheckin.checkedInAt")}:{" "}
                      {new Date(
                        result.attendance.checked_in_at
                      ).toLocaleTimeString(locale)}
                    </p>
                  )}
                  {result.attendance.checked_out_at && (
                    <p>
                      {t("selfCheckin.checkedOutAt")}:{" "}
                      {new Date(
                        result.attendance.checked_out_at
                      ).toLocaleTimeString(locale)}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const SessionDetail = () => {
  const { t, locale } = useLanguage();
  const { addNotification } = useNotification();
  const { id, sessionId } = useParams<{ id: string; sessionId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const backendRole = getCurrentBackendRole();
  const isTrainer = backendRole === "TRAINER";
  const canViewSessionAudit = backendRole === "MANAGER";
  const canExportAttendance =
    backendRole === "MANAGER" ||
    backendRole === "TEACHER" ||
    backendRole === "TRAINER" ||
    backendRole === "SYSTEM_ADMIN";
  // Matches backend sessions.activate policy: MANAGER/TRAINER/TEACHER (SYSTEM_ADMIN implicitly covers all roles).
  const canStartSession =
    backendRole === "MANAGER" ||
    backendRole === "TEACHER" ||
    backendRole === "TRAINER" ||
    backendRole === "SYSTEM_ADMIN";

  const [loading, setLoading] = React.useState(true);
  const [working, setWorking] = React.useState(false);
  const [loadingAttendance, setLoadingAttendance] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [togglingOutsideCheckin, setTogglingOutsideCheckin] =
    React.useState(false);

  const [classData, setClassData] = React.useState<ClassItem | null>(null);
  const [sessionData, setSessionData] = React.useState<ClassSession | null>(
    null
  );
  const [participants, setParticipants] = React.useState<SessionParticipant[]>(
    []
  );
  const [enrollments, setEnrollments] = React.useState<ClassEnrollment[]>([]);
  const [venues, setVenues] = React.useState<VenueItem[]>([]);
  const [attendanceRows, setAttendanceRows] = React.useState<AttendanceItem[]>(
    []
  );
  const [showCaptureModal, setShowCaptureModal] = React.useState(false);
  const [showSelfCheckModal, setShowSelfCheckModal] = React.useState(false);
  const [showNotifyModal, setShowNotifyModal] = React.useState(false);
  const [showReportForm, setShowReportForm] = React.useState(false);
  const [myReport, setMyReport] = React.useState<SessionReport | null>(null);
  const [editReport, setEditReport] = React.useState<SessionReport | null>(
    null
  );
  const [auditExpanded, setAuditExpanded] = React.useState(false);
  const [loadingSessionAudit, setLoadingSessionAudit] = React.useState(false);
  const [sessionAuditError, setSessionAuditError] = React.useState<
    string | null
  >(null);
  const [sessionAuditEvents, setSessionAuditEvents] = React.useState<
    ClassAuditEvent[]
  >([]);
  const [expandedSessionAuditEventIds, setExpandedSessionAuditEventIds] =
    React.useState<number[]>([]);
  const [studentSearchKeyword, setStudentSearchKeyword] = React.useState("");
  const [studentStatusFilter, setStudentStatusFilter] =
    React.useState<AttendanceStatusFilter>("ALL");
  const [attendanceReportTab, setAttendanceReportTab] = React.useState<
    "students" | "staff"
  >("students");
  const [sessionAuditEmailFilter, setSessionAuditEmailFilter] =
    React.useState("");
  const [sessionAuditActionFilter, setSessionAuditActionFilter] =
    React.useState("ALL");
  const [sessionAuditSessionFilter, setSessionAuditSessionFilter] =
    React.useState("ALL");
  const reloadMyReport = React.useCallback(async () => {
    if (!sessionId) return;
    try {
      const reports = await reportService.getForSession(sessionId);
      const me = authService.getStoredUser();
      // Find the report authored by the current user (if any)
      const mine = me
        ? reports.find((r) => r.trainer_id === me.id) ?? null
        : null;
      setMyReport(mine);
    } catch {
      // Silently ignore — report section will show empty
    }
  }, [sessionId]);

  React.useEffect(() => {
    if (!id || !sessionId) return;

    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        const [
          classItem,
          targetSession,
          sessionParticipants,
          classEnrollments,
        ] = await Promise.all([
          classMgmtService.getById(id),
          classMgmtService.getSessionById(sessionId),
          classMgmtService.getSessionParticipants(sessionId),
          classMgmtService.getEnrollments(id),
        ]);

        setClassData(classItem);
        setSessionData(targetSession);
        setParticipants(sessionParticipants);
        setEnrollments(classEnrollments);

        if (classItem.center_id) {
          const venueRes = await venueService.getAll({
            center_id: classItem.center_id,
            limit: 200,
          });
          setVenues(venueRes.items);
        }
      } catch (err: any) {
        setError(err.message ?? t("sessionDetail.loadError"));
      } finally {
        setLoading(false);
      }
    };

    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetching on language change is unnecessary
  }, [id, sessionId]);

  React.useEffect(() => {
    if (!id || !sessionId || !canViewSessionAudit) {
      setSessionAuditEvents([]);
      setSessionAuditError(null);
      setExpandedSessionAuditEventIds([]);
      setSessionAuditEmailFilter("");
      setSessionAuditActionFilter("ALL");
      setSessionAuditSessionFilter("ALL");
      return;
    }

    if (!auditExpanded) {
      return;
    }

    const loadSessionAudit = async () => {
      setLoadingSessionAudit(true);
      setSessionAuditError(null);
      try {
        const response = await classMgmtService.getAuditEvents(id, {
          session_id: sessionId,
          skip: 0,
          limit: 200,
        });
        const filtered = response.items.sort(
          (a, b) =>
            new Date(b.occurred_at).getTime() -
            new Date(a.occurred_at).getTime()
        );
        setSessionAuditEvents(filtered);
      } catch (err: any) {
        setSessionAuditEvents([]);
        setSessionAuditError(
          err.message ?? t("sessionDetailPage.auditLoadFailed")
        );
      } finally {
        setLoadingSessionAudit(false);
      }
    };

    void loadSessionAudit();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetching on language change is unnecessary
  }, [auditExpanded, canViewSessionAudit, id, sessionId]);

  React.useEffect(() => {
    if (!auditExpanded) {
      setExpandedSessionAuditEventIds([]);
    }
  }, [auditExpanded]);

  const sessionAuditActionOptions = React.useMemo(() => {
    return Array.from(
      new Set(sessionAuditEvents.map((event) => event.action_type))
    ).sort();
  }, [sessionAuditEvents]);

  const sessionAuditSessionOptions = React.useMemo(() => {
    const uniqueSessionIds = Array.from(
      new Set(
        sessionAuditEvents
          .map((event) => event.session_id)
          .filter(Boolean) as string[]
      )
    );

    return uniqueSessionIds.map((itemSessionId) => {
      const label =
        itemSessionId === sessionId
          ? sessionData?.title ?? itemSessionId
          : itemSessionId;
      return {
        value: itemSessionId,
        label,
      };
    });
  }, [sessionAuditEvents, sessionData?.title, sessionId]);

  const filteredSessionAuditEvents = React.useMemo(() => {
    const emailKeyword = sessionAuditEmailFilter.trim().toLowerCase();

    return sessionAuditEvents.filter((event) => {
      const actor = (
        event.actor_email ??
        event.actor_user_id ??
        ""
      ).toLowerCase();
      if (emailKeyword && !actor.includes(emailKeyword)) {
        return false;
      }

      if (
        sessionAuditActionFilter !== "ALL" &&
        event.action_type !== sessionAuditActionFilter
      ) {
        return false;
      }

      if (sessionAuditSessionFilter === "ALL") {
        return true;
      }

      return event.session_id === sessionAuditSessionFilter;
    });
  }, [
    sessionAuditActionFilter,
    sessionAuditEmailFilter,
    sessionAuditEvents,
    sessionAuditSessionFilter,
  ]);

  // Fetch current user's report for this session
  React.useEffect(() => {
    void reloadMyReport();
  }, [reloadMyReport]);

  const selectedVenue = React.useMemo(() => {
    if (!sessionData?.venue_id) return null;
    return venues.find((venue) => venue.id === sessionData.venue_id) ?? null;
  }, [venues, sessionData]);

  const selectedVenueLocation = React.useMemo(
    () => parseLocation(selectedVenue?.location ?? sessionData?.location),
    [selectedVenue, sessionData]
  );

  const googleMapsUrl = React.useMemo(() => {
    if (!selectedVenueLocation) return null;
    return `https://www.google.com/maps?q=${selectedVenueLocation.lat},${selectedVenueLocation.lng}`;
  }, [selectedVenueLocation]);

  const hasVenueInfo = React.useMemo(() => {
    return Boolean(
      selectedVenue?.name ||
        selectedVenue?.address ||
        selectedVenue?.location ||
        sessionData?.location
    );
  }, [selectedVenue, sessionData?.location]);

  const teacherParticipants = React.useMemo(() => {
    const seen = new Set<string>();
    return participants.filter((participant) => {
      if (participant.role !== "teacher") return false;
      if (seen.has(participant.user_id)) return false;
      seen.add(participant.user_id);
      return true;
    });
  }, [participants]);

  const trainerParticipants = React.useMemo(() => {
    const seen = new Set<string>();
    return participants.filter((participant) => {
      if (participant.role !== "trainer") return false;
      if (seen.has(participant.user_id)) return false;
      seen.add(participant.user_id);
      return true;
    });
  }, [participants]);

  const studentParticipants = React.useMemo(() => {
    return participants.filter((participant) => participant.role === "student");
  }, [participants]);

  const studentCards = React.useMemo(() => {
    return studentParticipants.map((participant) => {
      const enrollment = enrollments.find(
        (item) => item.student_id === participant.user_id
      );
      return {
        id: participant.id,
        userId: participant.user_id,
        user: participant.user ?? enrollment?.student,
        enrollment,
      };
    });
  }, [enrollments, studentParticipants]);

  const attendanceStatusByUserId = React.useMemo(() => {
    const statusByUserId = new Map<string, string>();

    participants.forEach((participant) => {
      if (participant.status) {
        statusByUserId.set(participant.user_id, participant.status);
      }
    });

    attendanceRows.forEach((row) => {
      if (row.checked_out_at) {
        statusByUserId.set(row.user_id, "checked_out");
      } else {
        statusByUserId.set(row.user_id, "checked_in");
      }
    });

    return statusByUserId;
  }, [attendanceRows, participants]);

  const attendanceTimeByUserId = React.useMemo(() => {
    const timeByUserId = new Map<
      string,
      { checkedInAt: string | null; checkedOutAt: string | null }
    >();
    participants.forEach((p) => {
      if (p.checked_in_at || p.checked_out_at) {
        timeByUserId.set(p.user_id, {
          checkedInAt: p.checked_in_at ?? null,
          checkedOutAt: p.checked_out_at ?? null,
        });
      }
    });
    attendanceRows.forEach((row) => {
      timeByUserId.set(row.user_id, {
        checkedInAt: row.checked_in_at ?? null,
        checkedOutAt: row.checked_out_at ?? null,
      });
    });
    return timeByUserId;
  }, [attendanceRows, participants]);

  const filteredStudentCards = React.useMemo(() => {
    const keyword = studentSearchKeyword.trim().toLowerCase();

    return studentCards.filter((student) => {
      if (keyword) {
        const fullName = student.user?.full_name?.toLowerCase() ?? "";
        const email = student.user?.email?.toLowerCase() ?? "";
        if (!fullName.includes(keyword) && !email.includes(keyword))
          return false;
      }
      if (studentStatusFilter !== "ALL") {
        const status = (
          attendanceStatusByUserId.get(student.userId) ?? "unmarked"
        ).toLowerCase();
        if (status !== studentStatusFilter) return false;
      }
      return true;
    });
  }, [
    studentCards,
    studentSearchKeyword,
    studentStatusFilter,
    attendanceStatusByUserId,
  ]);

  const attendanceParticipants = React.useMemo(() => {
    // Build a combined list: teachers first, then trainers, then students
    const result: Array<{
      userId: string;
      fullName: string;
      email?: string | null;
      status: string | null;
      role: ParticipantRole;
      roleLabel: string;
      checkedInAt: string | null;
      checkedOutAt: string | null;
    }> = [];

    teacherParticipants.forEach((p) => {
      const times = attendanceTimeByUserId.get(p.user_id);
      result.push({
        userId: p.user_id,
        fullName: p.user?.full_name ?? p.user?.email ?? t("enum.role.TEACHER"),
        email: p.user?.email,
        status: attendanceStatusByUserId.get(p.user_id) ?? null,
        role: "TEACHER",
        roleLabel: t("enum.role.TEACHER"),
        checkedInAt: times?.checkedInAt ?? null,
        checkedOutAt: times?.checkedOutAt ?? null,
      });
    });

    trainerParticipants.forEach((p) => {
      const times = attendanceTimeByUserId.get(p.user_id);
      result.push({
        userId: p.user_id,
        fullName: p.user?.full_name ?? p.user?.email ?? t("enum.role.TRAINER"),
        email: p.user?.email,
        status: attendanceStatusByUserId.get(p.user_id) ?? null,
        role: "TRAINER",
        roleLabel: t("enum.role.TRAINER"),
        checkedInAt: times?.checkedInAt ?? null,
        checkedOutAt: times?.checkedOutAt ?? null,
      });
    });

    studentCards.forEach((student) => {
      const times = attendanceTimeByUserId.get(student.userId);
      result.push({
        userId: student.userId,
        fullName:
          student.user?.full_name ??
          student.user?.email ??
          t("enum.role.STUDENT"),
        email: student.user?.email,
        status: attendanceStatusByUserId.get(student.userId) ?? null,
        role: "STUDENT",
        roleLabel: t("enum.role.STUDENT"),
        checkedInAt: times?.checkedInAt ?? null,
        checkedOutAt: times?.checkedOutAt ?? null,
      });
    });

    return result;
  }, [
    attendanceStatusByUserId,
    attendanceTimeByUserId,
    studentCards,
    t,
    teacherParticipants,
    trainerParticipants,
  ]);

  const canEditSession = sessionData?.status !== "CLOSED";

  // Trainers can only see themselves in the attendance modal
  const trainerSelfParticipants = React.useMemo(() => {
    if (!isTrainer) return attendanceParticipants;
    const me = authService.getStoredUser();
    if (!me) return [];
    return attendanceParticipants.filter((p) => p.userId === me.id);
  }, [isTrainer, attendanceParticipants]);

  const shouldShowAttendanceButton = sessionData?.status !== "SCHEDULED";

  const reloadAttendance = React.useCallback(async () => {
    if (!sessionId) return;
    setLoadingAttendance(true);
    try {
      const rows = await teachingAttendanceService.getSessionAttendance(
        sessionId
      );
      setAttendanceRows(rows);
    } finally {
      setLoadingAttendance(false);
    }
  }, [sessionId]);

  React.useEffect(() => {
    if (
      sessionData?.status === "ACTIVE" ||
      sessionData?.status === "COMPLETED" ||
      sessionData?.status === "CLOSED"
    ) {
      reloadAttendance().catch(() => {});
      return;
    }

    setAttendanceRows([]);
  }, [reloadAttendance, sessionData?.status]);

  const [quickCheckingUserId, setQuickCheckingUserId] = React.useState<
    string | null
  >(null);

  const handleQuickCheck = React.useCallback(
    async (userId: string) => {
      if (!sessionId) return;
      setQuickCheckingUserId(userId);
      try {
        await teachingAttendanceService.submitManualAttendance({
          user_id: userId,
          session_id: sessionId,
          action: "check",
        });
        await reloadAttendance();
        const updated = await classMgmtService.getSessionParticipants(
          sessionId
        );
        setParticipants(updated);
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : t("sessionDetailPage.attendance.failed");
        addNotification(message, "error");
      } finally {
        setQuickCheckingUserId(null);
      }
    },
    [sessionId, reloadAttendance, addNotification, t]
  );

  const attendanceReportGroups = React.useMemo(() => {
    const buildGroup = (rows: typeof attendanceParticipants) => {
      const counts = {
        total: rows.length,
        checkedIn: 0,
        checkedOut: 0,
        absent: 0,
        excused: 0,
        unmarked: 0,
      };
      rows.forEach((row) => {
        const status = row.status?.toLowerCase();
        if (status === "checked_in") counts.checkedIn += 1;
        else if (status === "checked_out") counts.checkedOut += 1;
        else if (status === "absent") counts.absent += 1;
        else if (status === "excused_absence") counts.excused += 1;
        else counts.unmarked += 1;
      });
      return { rows, counts };
    };

    return {
      students: buildGroup(
        attendanceParticipants.filter((row) => row.role === "STUDENT")
      ),
      staff: buildGroup(
        attendanceParticipants.filter((row) => row.role !== "STUDENT")
      ),
    };
  }, [attendanceParticipants]);

  const activateSessionAndRefresh = React.useCallback(async () => {
    if (!sessionId) return false;

    await teachingSessionService.activateSession(sessionId);
    const [updatedSession, sessionParticipants] = await Promise.all([
      classMgmtService.getSessionById(sessionId),
      classMgmtService.getSessionParticipants(sessionId),
    ]);
    setSessionData(updatedSession);
    setParticipants(sessionParticipants);
    return true;
  }, [sessionId]);

  const handleToggleOutsideCheckin = React.useCallback(async () => {
    if (!sessionId || !sessionData) return;
    setTogglingOutsideCheckin(true);
    try {
      const updated = await classMgmtService.updateSession(sessionId, {
        allow_outside_checkin: !sessionData.allow_outside_checkin,
      });
      setSessionData(updated);
    } catch (err) {
      addNotification(
        err instanceof Error
          ? err.message
          : t("sessionDetailPage.updateSettingsFailed"),
        "error"
      );
    } finally {
      setTogglingOutsideCheckin(false);
    }
  }, [sessionId, sessionData, addNotification, t]);

  const handleStartSession = React.useCallback(async () => {
    setWorking(true);
    try {
      await activateSessionAndRefresh();
      addNotification(t("sessionDetailPage.sessionStarted"), "success");
    } catch (err: any) {
      alert(err.message ?? t("sessionDetailPage.startFailed"));
    } finally {
      setWorking(false);
    }
  }, [activateSessionAndRefresh, addNotification, t]);

  const handleOpenAttendanceCapture = React.useCallback(async () => {
    if (!sessionData || !sessionId) return;
    if (attendanceParticipants.length === 0) return;

    if (sessionData.status === "SCHEDULED") {
      const confirmed = window.confirm(
        t("sessionDetailPage.confirmStartAndAttend")
      );
      if (!confirmed) return;

      setWorking(true);
      try {
        await activateSessionAndRefresh();
        setShowCaptureModal(true);
      } catch (err: any) {
        alert(err.message ?? t("sessionDetailPage.startFailed"));
      } finally {
        setWorking(false);
      }
      return;
    }

    setShowCaptureModal(true);
  }, [
    activateSessionAndRefresh,
    attendanceParticipants.length,
    sessionData,
    sessionId,
    t,
  ]);

  const handleBack = React.useCallback(() => {
    const fromState = (location.state as { from?: string } | null)?.from;
    if (fromState) {
      navigate(fromState);
      return;
    }

    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate("/admin/sessions");
  }, [location.state, navigate]);

  if (loading) {
    return (
      <div className="mt-5 text-sm text-ink-muted">
        {t("sessionDetail.loading")}
      </div>
    );
  }

  if (error || !classData || !sessionData) {
    return (
      <div className="mt-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-900/10 dark:text-red-400">
        {error ?? t("sessionDetailPage.notFound")}
      </div>
    );
  }

  const canSubmitReport =
    sessionData?.status === "ACTIVE" ||
    sessionData?.status === "COMPLETED" ||
    sessionData?.status === "CLOSED";

  const handleExportAttendance = async () => {
    if (!sessionId) return;
    try {
      const BASE_URL =
        process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1";
      const token = sessionStorage.getItem("access_token");
      const res = await fetch(
        `${BASE_URL}/sessions/${sessionId}/export-attendance`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      if (!res.ok) throw new Error(`Export failed: ${res.status}`);
      const blob = await res.blob();
      const disposition = res.headers.get("Content-Disposition");
      const match = disposition?.match(/filename="?(.+?)"?$/);
      const filename = match?.[1] || `attendance_${sessionId}.xlsx`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export attendance error:", err);
    }
  };

  if (showReportForm && sessionId) {
    return (
      <ReportSubmitForm
        sessionId={sessionId}
        existingReport={editReport}
        onCancel={() => {
          setShowReportForm(false);
          setEditReport(null);
        }}
        onSaved={() => {
          setShowReportForm(false);
          setEditReport(null);
          void reloadMyReport();
        }}
      />
    );
  }

  return (
    <div className="mt-5 space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button
            onClick={handleBack}
            className="rounded-lg border border-line p-2 text-ink-muted hover:bg-surface-hover"
          >
            <MdArrowBack className="h-5 w-5" />
          </button>
          <div>
            <h2 className="text-2xl font-bold text-ink">
              {t("sessionDetail.title")}
            </h2>
            <p className="text-sm text-ink-muted">
              {t("sessionDetailPage.classLabel", { name: classData.name })}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {canSubmitReport && !myReport && (
            <button
              type="button"
              onClick={() => {
                setEditReport(null);
                setShowReportForm(true);
              }}
              className="flex items-center gap-1.5 rounded-lg border border-green-500 px-4 py-2 text-sm font-medium text-green-600 hover:bg-green-50 dark:hover:bg-green-900/10 dark:text-green-300"
            >
              <MdAssignment className="h-4 w-4" />
              {t("sessionDetailPage.submitReport")}
            </button>
          )}
          {canSubmitReport && myReport && (
            <button
              type="button"
              onClick={() => {
                setEditReport(myReport);
                setShowReportForm(true);
              }}
              className="flex items-center gap-1.5 rounded-lg border border-brand-500 px-4 py-2 text-sm font-medium text-brand-500 hover:bg-brand-50 dark:hover:bg-brand-900/10"
            >
              <MdEdit className="h-4 w-4" />
              {t("sessionDetailPage.editReport")}
            </button>
          )}
          {canStartSession && sessionData.status === "SCHEDULED" && (
            <button
              type="button"
              disabled={working}
              onClick={() => {
                void handleStartSession();
              }}
              className="flex items-center gap-1.5 rounded-lg border border-green-500 bg-green-500 px-4 py-2 text-sm font-medium text-white hover:bg-green-600 disabled:opacity-60"
            >
              <MdPlayArrow className="h-4 w-4" />
              {t("sessionDetailPage.startSession")}
            </button>
          )}
          {canExportAttendance && (
            <button
              type="button"
              onClick={() => {
                void handleExportAttendance();
              }}
              className="flex items-center gap-1.5 rounded-lg border border-brand-500 px-4 py-2 text-sm font-medium text-brand-500 hover:bg-brand-50 dark:hover:bg-brand-900/10"
            >
              <MdFileDownload className="h-4 w-4" />
              {t("sessionDetailPage.exportAttendance")}
            </button>
          )}
          <button
            type="button"
            onClick={() => setShowNotifyModal(true)}
            className="flex items-center gap-1.5 rounded-lg border border-orange-500 px-4 py-2 text-sm font-medium text-orange-500 hover:bg-orange-50 dark:hover:bg-orange-900/10"
          >
            <MdNotifications className="h-4 w-4" />
            {t("notify.sendBtn")}
          </button>
          {canEditSession ? (
            <button
              type="button"
              onClick={() =>
                navigate(`/admin/classes-mgmt/${id}/sessions/${sessionId}/edit`)
              }
              className="flex items-center gap-1.5 rounded-lg border border-brand-500 px-4 py-2 text-sm font-medium text-brand-500 hover:bg-brand-50 dark:hover:bg-brand-900/10"
            >
              <MdEdit className="h-4 w-4" />
              {t("settings.edit")}
            </button>
          ) : null}
        </div>
      </div>

      <Card extra="p-6">
        <div className="mb-4 flex items-center gap-2">
          <MdCalendarMonth className="h-5 w-5 text-brand-500" />
          <h3 className="text-base font-semibold text-ink">
            {t("sessionDetailPage.sessionInfo")}
          </h3>
        </div>

        <div className="rounded-2xl border border-line bg-gray-50/50 p-4 dark:bg-white/5">
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1.15fr)_minmax(280px,0.85fr)]">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <p className="text-[11px] font-medium uppercase tracking-wide text-ink-muted">
                  {t("sessionDetailPage.status")}
                </p>
                <div className="mt-1">
                  <StatusBadge
                    label={t(statusBadgeKey(sessionData.status))}
                    tone={getSessionStatusTone(sessionData.status)}
                  />
                </div>
              </div>
              <div>
                <p className="text-[11px] font-medium uppercase tracking-wide text-ink-muted">
                  {t("sessionDetail.time")}
                </p>
                {(() => {
                  const { timeText, dateText } = formatSessionSchedule(
                    t,
                    locale,
                    sessionData.start_time,
                    sessionData.end_time
                  );
                  return (
                    <div className="mt-1">
                      <p className="text-sm font-semibold text-ink">
                        {timeText}
                      </p>
                      {dateText ? (
                        <p className="text-xs font-medium text-ink-muted">
                          {dateText}
                        </p>
                      ) : null}
                    </div>
                  );
                })()}
              </div>
              <div>
                <p className="text-[11px] font-medium uppercase tracking-wide text-ink-muted">
                  {t("sessionDetailPage.outsideCheckin")}
                </p>
                <div className="mt-1 flex items-center gap-2">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={sessionData.allow_outside_checkin}
                    disabled={togglingOutsideCheckin}
                    onClick={handleToggleOutsideCheckin}
                    className={`relative inline-flex h-6 w-10 items-center rounded-full transition disabled:cursor-not-allowed disabled:opacity-60 ${
                      sessionData.allow_outside_checkin
                        ? "bg-green-500"
                        : "bg-gray-300 dark:bg-surface-hover"
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                        sessionData.allow_outside_checkin
                          ? "translate-x-5"
                          : "translate-x-1"
                      }`}
                    />
                  </button>
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${
                      sessionData.allow_outside_checkin
                        ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300"
                        : "bg-gray-200 text-gray-600 dark:bg-surface-muted dark:text-gray-300"
                    }`}
                  >
                    {togglingOutsideCheckin
                      ? t("action.saving")
                      : sessionData.allow_outside_checkin
                      ? t("sessionDetailPage.toggleOn")
                      : t("sessionDetailPage.toggleOff")}
                  </span>
                </div>
              </div>
              {hasVenueInfo ? (
                <div className="sm:col-span-2">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-ink-muted">
                    {t("sessionDetailPage.venue")}
                  </p>
                  {selectedVenue?.name ? (
                    <p className="mt-1 text-sm font-semibold text-ink">
                      {selectedVenue.name}
                    </p>
                  ) : null}
                  {selectedVenue?.address ? (
                    <p className="mt-1 text-xs text-ink-muted">
                      {selectedVenue.address}
                    </p>
                  ) : null}
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-ink-muted">
                    {selectedVenue?.location || sessionData.location ? (
                      <span>
                        {selectedVenue?.location ?? sessionData.location}
                      </span>
                    ) : null}
                    {googleMapsUrl ? (
                      <a
                        href={googleMapsUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center rounded-full border border-brand-200 px-2.5 py-1 text-[11px] font-semibold text-brand-600 hover:bg-brand-50 dark:border-brand-500/40 dark:text-brand-300 dark:hover:bg-brand-500/10"
                      >
                        Google Maps
                      </a>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>

            <div className="space-y-3">
              <div>
                <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-ink-muted">
                  {t("enum.role.TEACHER")}
                </p>
                {teacherParticipants.length > 0 ? (
                  <div className="divide-y divide-line rounded-xl border border-gray-200/70 bg-white/70 px-3 dark:border-white/10 dark:bg-surface/40">
                    {teacherParticipants.map((participant) => {
                      const times = attendanceTimeByUserId.get(
                        participant.user_id
                      );
                      return (
                        <CompactParticipantRow
                          key={participant.id}
                          user={participant.user}
                          fallbackLabel={t("enum.role.TEACHER")}
                          attendanceStatus={
                            attendanceStatusByUserId.get(participant.user_id) ??
                            null
                          }
                          checkedInAt={times?.checkedInAt ?? null}
                          checkedOutAt={times?.checkedOutAt ?? null}
                          onCheck={
                            shouldShowAttendanceButton
                              ? () => handleQuickCheck(participant.user_id)
                              : undefined
                          }
                          checking={quickCheckingUserId === participant.user_id}
                        />
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-xs text-ink-muted">
                    {t("sessionDetailPage.noTeacher")}
                  </p>
                )}
              </div>

              <div>
                <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-ink-muted">
                  {t("enum.role.TRAINER")}
                </p>
                {trainerParticipants.length > 0 ? (
                  <div className="divide-y divide-line rounded-xl border border-gray-200/70 bg-white/70 px-3 dark:border-white/10 dark:bg-surface/40">
                    {trainerParticipants.map((participant) => {
                      const times = attendanceTimeByUserId.get(
                        participant.user_id
                      );
                      return (
                        <CompactParticipantRow
                          key={participant.id}
                          user={participant.user}
                          fallbackLabel={t("enum.role.TRAINER")}
                          attendanceStatus={
                            attendanceStatusByUserId.get(participant.user_id) ??
                            null
                          }
                          checkedInAt={times?.checkedInAt ?? null}
                          checkedOutAt={times?.checkedOutAt ?? null}
                          onCheck={
                            shouldShowAttendanceButton
                              ? () => handleQuickCheck(participant.user_id)
                              : undefined
                          }
                          checking={quickCheckingUserId === participant.user_id}
                        />
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-xs text-ink-muted">
                    {t("sessionDetailPage.noTrainer")}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </Card>

      <Card extra="p-6">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <h3 className="text-base font-semibold text-ink">
              {t("sessionAttendanceReport.students")}
            </h3>
            <span className="flex-shrink-0 rounded-full bg-surface-muted px-2 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-400">
              {filteredStudentCards.length}
              {filteredStudentCards.length !== studentCards.length
                ? `/${studentCards.length}`
                : ""}
            </span>
            <div className="relative ml-2 w-full max-w-xs">
              <MdSearch className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-subtle" />
              <input
                type="text"
                value={studentSearchKeyword}
                onChange={(event) =>
                  setStudentSearchKeyword(event.target.value)
                }
                placeholder={t("sessionDetailPage.searchPlaceholder")}
                className="w-full rounded-lg border border-line bg-surface py-1.5 pl-8 pr-2.5 text-xs text-ink outline-none focus:border-brand-500"
              />
            </div>
            <select
              value={studentStatusFilter}
              onChange={(e) =>
                setStudentStatusFilter(e.target.value as AttendanceStatusFilter)
              }
              className="ml-2 rounded-lg border border-line bg-surface px-2 py-1.5 text-xs text-ink outline-none focus:border-brand-500"
            >
              <option value="ALL">{t("sessionDetailPage.allStatuses")}</option>
              {ATTENDANCE_FILTER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {t(opt.labelKey)}
                </option>
              ))}
            </select>
          </div>
          {shouldShowAttendanceButton ? (
            <div className="flex items-center gap-2">
              <span className="text-xs text-ink-muted">
                {t("sessionDetailPage.attendedCount", {
                  done: attendanceRows.length,
                  total: enrollments.length,
                })}
                {loadingAttendance ? ` • ${t("state.loading")}` : ""}
              </span>
              <button
                type="button"
                disabled={attendanceParticipants.length === 0 || working}
                onClick={() => {
                  void handleOpenAttendanceCapture();
                }}
                className="inline-flex items-center gap-1 rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <MdCameraAlt className="h-4 w-4" />
                {t("sessionDetailPage.takeAttendance")}
              </button>
              <button
                type="button"
                disabled={working}
                onClick={() => setShowSelfCheckModal(true)}
                className="inline-flex items-center gap-1 rounded-lg bg-green-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-green-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <MdCameraFront className="h-4 w-4" />
                {t("selfCheckin.title")}
              </button>
            </div>
          ) : null}
        </div>

        <div className="rounded-2xl border border-line bg-surface p-4">
          <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
            {studentCards.length === 0 ? (
              <p className="rounded-xl border border-dashed border-line px-3 py-5 text-center text-xs text-ink-muted">
                {t("sessionDetailPage.noStudents")}
              </p>
            ) : filteredStudentCards.length === 0 ? (
              <p className="rounded-xl border border-dashed border-line px-3 py-5 text-center text-xs text-ink-muted">
                {t("sessionDetailPage.noStudentMatch")}
              </p>
            ) : (
              filteredStudentCards.map((student) => (
                <div
                  key={student.id}
                  className="flex items-center justify-between gap-3 rounded-xl border border-line bg-white px-2.5 py-2 dark:bg-surface-muted/40"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <UserAvatar user={student.user} size="xs" />
                    <p className="truncate text-xs text-gray-600 dark:text-gray-300">
                      <span className="font-semibold text-ink">
                        {student.user?.full_name ??
                          student.enrollment?.student_id.slice(0, 8) ??
                          t("enum.role.STUDENT")}
                      </span>
                      <span className="ml-2">
                        {student.user?.email ?? t("sessionDetailPage.noEmail")}
                      </span>
                      <span className="ml-2 hidden lg:inline">
                        •{" "}
                        {t("sessionDetailPage.dob", {
                          dob: formatDob(t, locale, student.user?.dob),
                        })}
                      </span>
                      <span className="ml-2 hidden lg:inline">
                        •{" "}
                        {t("sessionDetailPage.remainingSessions", {
                          count: student.enrollment?.remaining_sessions ?? "?",
                        })}
                      </span>
                    </p>
                  </div>
                  <div className="flex flex-shrink-0 items-center gap-2">
                    {(() => {
                      const studentAttendanceStatus =
                        attendanceStatusByUserId.get(student.userId) ?? null;
                      const normalizedStudentStatus = (
                        studentAttendanceStatus ?? ""
                      ).toLowerCase();
                      const studentTimes = attendanceTimeByUserId.get(
                        student.userId
                      );
                      const inTime = formatAttendanceTime(
                        studentTimes?.checkedInAt,
                        locale
                      );
                      const outTime = formatAttendanceTime(
                        studentTimes?.checkedOutAt,
                        locale
                      );
                      const isCheckedOut =
                        normalizedStudentStatus === "checked_out";
                      const buttonAction =
                        !normalizedStudentStatus ||
                        normalizedStudentStatus === "absent" ||
                        normalizedStudentStatus === "excused_absence"
                          ? "in"
                          : normalizedStudentStatus === "checked_in"
                          ? "out"
                          : null;
                      const isCheckingThis =
                        quickCheckingUserId === student.userId;
                      return (
                        <>
                          {inTime && (
                            <span className="border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200 rounded-full border px-2 py-0.5 text-[11px] font-medium">
                              {t("sessionDetailPage.attendance.inAt", {
                                time: inTime,
                              })}
                            </span>
                          )}
                          {outTime && (
                            <span className="border-sky-300 bg-sky-50 text-sky-700 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-200 rounded-full border px-2 py-0.5 text-[11px] font-medium">
                              {t("sessionDetailPage.attendance.outAt", {
                                time: outTime,
                              })}
                            </span>
                          )}
                          <span
                            className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${getAttendanceStatusBadgeClass(
                              studentAttendanceStatus
                            )}`}
                          >
                            {attendanceStatusLabel(t, studentAttendanceStatus)}
                          </span>
                          {shouldShowAttendanceButton &&
                            !isCheckedOut &&
                            buttonAction && (
                              <button
                                type="button"
                                disabled={isCheckingThis}
                                onClick={() => handleQuickCheck(student.userId)}
                                className={`ml-1 rounded-lg px-2 py-0.5 text-[10px] font-semibold text-white disabled:opacity-50 ${
                                  buttonAction === "in"
                                    ? "bg-emerald-500 hover:bg-emerald-600"
                                    : "bg-sky-500 hover:bg-sky-600"
                                }`}
                              >
                                {isCheckingThis
                                  ? "..."
                                  : t(
                                      `sessionDetailPage.attendance.${buttonAction}`
                                    )}
                              </button>
                            )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </Card>

      {canExportAttendance ? (
        <Card extra="overflow-hidden">
          <div className="relative overflow-hidden bg-gradient-to-r from-brand-600 via-brand-500 to-cyan-500 px-6 py-5 text-white">
            <div className="absolute -right-8 -top-12 h-36 w-36 rounded-full bg-white/10" />
            <div className="absolute -bottom-16 right-24 h-32 w-32 rounded-full bg-white/10" />
            <div className="relative flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/20 shadow-sm backdrop-blur-sm">
                  <MdAssessment className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-lg font-bold">
                    {t("sessionAttendanceReport.title")}
                  </h3>
                  <p className="text-xs text-white/80">
                    {t("sessionAttendanceReport.subtitle")}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="rounded-xl bg-white/15 px-4 py-2 text-right backdrop-blur-sm">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-white/70">
                    {t("sessionAttendanceReport.attendanceRate")}
                  </p>
                  <p className="text-xl font-bold">
                    {attendanceReportGroups[attendanceReportTab].counts.total >
                    0
                      ? Math.round(
                          ((attendanceReportGroups[attendanceReportTab].counts
                            .checkedIn +
                            attendanceReportGroups[attendanceReportTab].counts
                              .checkedOut) /
                            attendanceReportGroups[attendanceReportTab].counts
                              .total) *
                            100
                        )
                      : 0}
                    %
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    void handleExportAttendance();
                  }}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-white px-4 py-2.5 text-xs font-bold text-brand-600 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                >
                  <MdFileDownload className="h-4 w-4" />
                  {t("sessionAttendanceReport.export")}
                </button>
              </div>
            </div>
          </div>

          <div className="p-6">
            <div className="mb-5 inline-flex rounded-xl bg-surface-muted p-1">
              {(["students", "staff"] as const).map((group) => (
                <button
                  key={group}
                  type="button"
                  onClick={() => setAttendanceReportTab(group)}
                  className={`rounded-lg px-5 py-2 text-sm font-semibold transition-all ${
                    attendanceReportTab === group
                      ? "bg-white text-brand-600 shadow-sm dark:bg-surface-muted dark:text-brand-300"
                      : "text-ink-muted hover:text-navy-700 dark:hover:text-white"
                  }`}
                >
                  {t(`sessionAttendanceReport.${group}`)}
                  <span
                    className={`ml-2 rounded-full px-2 py-0.5 text-[10px] ${
                      attendanceReportTab === group
                        ? "bg-brand-50 text-brand-600 dark:bg-brand-500/20 dark:text-brand-200"
                        : "bg-gray-200 text-ink-muted dark:bg-white/10"
                    }`}
                  >
                    {attendanceReportGroups[group].counts.total}
                  </span>
                </button>
              ))}
            </div>

            {(() => {
              const group = attendanceReportGroups[attendanceReportTab];
              const stats = [
                {
                  key: "checkedIn",
                  value: group.counts.checkedIn,
                  dot: "bg-emerald-500",
                  valueTone: "text-emerald-600 dark:text-emerald-300",
                  border: "border-emerald-100 dark:border-emerald-500/20",
                },
                {
                  key: "checkedOut",
                  value: group.counts.checkedOut,
                  dot: "bg-sky-500",
                  valueTone: "text-sky-600 dark:text-sky-300",
                  border: "border-sky-100 dark:border-sky-500/20",
                },
                {
                  key: "absent",
                  value: group.counts.absent,
                  dot: "bg-red-500",
                  valueTone: "text-red-600 dark:text-red-300",
                  border: "border-red-100 dark:border-red-500/20",
                },
                {
                  key: "excused",
                  value: group.counts.excused,
                  dot: "bg-amber-500",
                  valueTone: "text-amber-600 dark:text-amber-300",
                  border: "border-amber-100 dark:border-amber-500/20",
                },
                {
                  key: "unmarked",
                  value: group.counts.unmarked,
                  dot: "bg-gray-400",
                  valueTone: "text-gray-600 dark:text-gray-300",
                  border: "border-line",
                },
              ];

              return (
                <>
                  <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-5">
                    {stats.map((stat) => (
                      <div
                        key={stat.key}
                        className={`rounded-2xl border bg-surface p-4 shadow-sm ${stat.border}`}
                      >
                        <div className="mb-2 flex items-center justify-between">
                          <span
                            className={`h-2.5 w-2.5 rounded-full ${stat.dot}`}
                          />
                          <span className="text-[10px] font-bold uppercase tracking-wide text-ink-subtle">
                            {group.counts.total > 0
                              ? Math.round(
                                  (stat.value / group.counts.total) * 100
                                )
                              : 0}
                            %
                          </span>
                        </div>
                        <p className={`text-2xl font-bold ${stat.valueTone}`}>
                          {stat.value}
                        </p>
                        <p className="mt-0.5 text-xs font-medium text-ink-muted">
                          {t(`sessionAttendanceReport.${stat.key}`)}
                        </p>
                      </div>
                    ))}
                  </div>

                  <div className="overflow-x-auto rounded-2xl border border-line bg-surface">
                    <table className="w-full min-w-[720px] text-left text-sm">
                      <thead className="bg-gray-50/80 text-[11px] uppercase tracking-wider text-ink-muted dark:bg-white/5">
                        <tr>
                          <th className="w-14 px-4 py-3.5 text-center">#</th>
                          <th className="px-4 py-3.5">
                            {t("sessionAttendanceReport.person")}
                          </th>
                          {attendanceReportTab === "staff" ? (
                            <th className="px-4 py-3.5">
                              {t("sessionAttendanceReport.role")}
                            </th>
                          ) : null}
                          <th className="px-4 py-3.5">
                            {t("sessionAttendanceReport.status")}
                          </th>
                          <th className="px-4 py-3.5">
                            {t("sessionAttendanceReport.checkIn")}
                          </th>
                          <th className="px-4 py-3.5">
                            {t("sessionAttendanceReport.checkOut")}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-line">
                        {group.rows.length === 0 ? (
                          <tr>
                            <td
                              colSpan={attendanceReportTab === "staff" ? 6 : 5}
                              className="px-4 py-14 text-center text-ink-muted"
                            >
                              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-surface-muted">
                                <MdAssessment className="h-6 w-6 text-ink-subtle" />
                              </div>
                              {t("sessionAttendanceReport.empty")}
                            </td>
                          </tr>
                        ) : (
                          group.rows.map((row, index) => {
                            const initials = row.fullName
                              .split(" ")
                              .filter(Boolean)
                              .slice(-2)
                              .map((part) => part[0])
                              .join("")
                              .toUpperCase();
                            const checkInTime = formatAttendanceTime(
                              row.checkedInAt,
                              locale
                            );
                            const checkOutTime = formatAttendanceTime(
                              row.checkedOutAt,
                              locale
                            );
                            return (
                              <tr
                                key={row.userId}
                                className="text-ink-muted transition-colors hover:bg-gray-50/70 dark:hover:bg-white/[0.03]"
                              >
                                <td className="px-4 py-3.5 text-center text-xs font-medium text-ink-subtle">
                                  {String(index + 1).padStart(2, "0")}
                                </td>
                                <td className="px-4 py-3.5">
                                  <div className="flex items-center gap-3">
                                    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-brand-100 to-cyan-100 text-xs font-bold text-brand-700 dark:from-brand-500/20 dark:to-cyan-500/20 dark:text-brand-200">
                                      {initials || "?"}
                                    </div>
                                    <div className="min-w-0">
                                      <p className="truncate font-semibold text-ink">
                                        {row.fullName}
                                      </p>
                                      <p className="truncate text-xs text-ink-subtle">
                                        {row.email || "—"}
                                      </p>
                                    </div>
                                  </div>
                                </td>
                                {attendanceReportTab === "staff" ? (
                                  <td className="px-4 py-3.5">
                                    <span className="bg-violet-50 text-violet-600 dark:bg-violet-500/10 dark:text-violet-300 rounded-full px-2.5 py-1 text-xs font-semibold">
                                      {row.roleLabel}
                                    </span>
                                  </td>
                                ) : null}
                                <td className="px-4 py-3.5">
                                  <span
                                    className={`rounded-full px-2.5 py-1 text-xs font-semibold ${getAttendanceStatusBadgeClass(
                                      row.status
                                    )}`}
                                  >
                                    {attendanceStatusLabel(t, row.status)}
                                  </span>
                                </td>
                                <td className="px-4 py-3.5">
                                  {checkInTime ? (
                                    <span className="border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold">
                                      <span className="bg-emerald-500 h-1.5 w-1.5 rounded-full" />
                                      {t(
                                        "sessionAttendanceReport.checkInBadge"
                                      )}{" "}
                                      {checkInTime}
                                    </span>
                                  ) : (
                                    <span className="text-gray-300 dark:text-gray-600">
                                      —
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-3.5">
                                  {checkOutTime ? (
                                    <span className="border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-300 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold">
                                      <span className="bg-sky-500 h-1.5 w-1.5 rounded-full" />
                                      {t(
                                        "sessionAttendanceReport.checkOutBadge"
                                      )}{" "}
                                      {checkOutTime}
                                    </span>
                                  ) : (
                                    <span className="text-gray-300 dark:text-gray-600">
                                      —
                                    </span>
                                  )}
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              );
            })()}
          </div>
        </Card>
      ) : null}

      {canSubmitReport && sessionId && (
        <Card extra="p-6">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MdDescription className="h-5 w-5 text-brand-500" />
              <h3 className="text-base font-semibold text-ink">
                {t("sessionDetailPage.sessionReport")}
              </h3>
            </div>
            {!myReport && (
              <button
                type="button"
                onClick={() => {
                  setEditReport(null);
                  setShowReportForm(true);
                }}
                className="flex items-center gap-1 rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-600"
              >
                <MdAssignment className="h-4 w-4" />
                {t("sessionDetailPage.submitReport")}
              </button>
            )}
          </div>
          <ReportView
            sessionId={sessionId}
            onEdit={(report) => {
              setEditReport(report);
              setShowReportForm(true);
            }}
          />
        </Card>
      )}

      {showNotifyModal && sessionId && (
        <NotifySessionModal
          sessionId={sessionId}
          sessionTitle={sessionData?.title}
          onClose={() => setShowNotifyModal(false)}
        />
      )}

      {canViewSessionAudit ? (
        <Card extra="p-6">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <MdHistory className="h-5 w-5 text-brand-500" />
              <h3 className="text-base font-semibold text-ink">
                {t("sessionDetailPage.audit.title")}
                <span className="ml-2 rounded-full bg-surface-muted px-2 py-0.5 text-xs font-normal text-gray-600 dark:text-gray-400">
                  {filteredSessionAuditEvents.length}
                </span>
              </h3>
            </div>
            <button
              type="button"
              onClick={() => setAuditExpanded((prev) => !prev)}
              className="rounded-lg border border-line px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-surface-hover dark:text-gray-300"
            >
              {auditExpanded
                ? t("sessionDetailPage.audit.hide")
                : t("sessionDetailPage.audit.show")}
            </button>
          </div>

          {auditExpanded ? (
            loadingSessionAudit ? (
              <p className="pt-4 text-sm text-ink-muted">
                {t("sessionDetailPage.audit.loading")}
              </p>
            ) : sessionAuditError ? (
              <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-900/10 dark:text-red-400">
                {sessionAuditError}
              </div>
            ) : (
              <>
                <div className="mt-4 grid grid-cols-1 gap-2 md:grid-cols-3">
                  <input
                    type="text"
                    value={sessionAuditEmailFilter}
                    onChange={(event) =>
                      setSessionAuditEmailFilter(event.target.value)
                    }
                    placeholder={t("sessionDetailPage.audit.emailFilter")}
                    className="rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-brand-500"
                  />
                  <select
                    value={sessionAuditActionFilter}
                    onChange={(event) =>
                      setSessionAuditActionFilter(event.target.value)
                    }
                    className="rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-brand-500"
                  >
                    <option value="ALL">
                      {t("sessionDetailPage.audit.allActions")}
                    </option>
                    {sessionAuditActionOptions.map((actionType) => (
                      <option key={actionType} value={actionType}>
                        {formatSessionActionLabel(t, actionType)}
                      </option>
                    ))}
                  </select>
                  <select
                    value={sessionAuditSessionFilter}
                    onChange={(event) =>
                      setSessionAuditSessionFilter(event.target.value)
                    }
                    className="rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-brand-500"
                  >
                    <option value="ALL">
                      {t("sessionDetailPage.audit.allSessions")}
                    </option>
                    {sessionAuditSessionOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {sessionAuditEvents.length === 0 ? (
                  <p className="pt-4 text-sm text-ink-muted">
                    {t("sessionDetailPage.audit.empty")}
                  </p>
                ) : filteredSessionAuditEvents.length === 0 ? (
                  <p className="pt-4 text-sm text-ink-muted">
                    {t("sessionDetailPage.audit.noMatch")}
                  </p>
                ) : (
                  <div className="mt-3 max-h-[420px] overflow-y-auto border-l border-line pl-4 pr-1">
                    {filteredSessionAuditEvents.map((event) => {
                      const actorDisplay =
                        event.actor_email ??
                        event.actor_user_id ??
                        t("sessionDetailPage.audit.system");
                      const normalizedEntityType = event.entity_type
                        .trim()
                        .toLowerCase();
                      const targetName =
                        normalizedEntityType === "session"
                          ? sessionData?.title ?? event.entity_id
                          : normalizedEntityType === "class"
                          ? classData?.name ?? event.entity_id
                          : event.entity_id;
                      const affectedEntity = `${formatSessionAuditEntityType(
                        t,
                        event.entity_type
                      )}: ${targetName}`;
                      const isExpanded = expandedSessionAuditEventIds.includes(
                        event.id
                      );
                      const changedFieldRows = getSessionChangedFieldRows(
                        t,
                        locale,
                        event
                      );

                      return (
                        <div
                          key={event.id}
                          className="relative mb-2 rounded-xl border border-line bg-surface px-3 py-2.5 text-sm"
                        >
                          <span className="absolute -left-[22px] top-3 h-2.5 w-2.5 rounded-full bg-brand-500" />
                          <div className="flex items-start justify-between gap-3">
                            <p className="flex min-w-0 flex-wrap items-center gap-2 text-sm text-ink">
                              <span className="text-xs text-ink-muted">
                                {new Date(event.occurred_at).toLocaleString(
                                  locale
                                )}
                              </span>
                              <span className="font-semibold">
                                {actorDisplay}
                              </span>
                              <span className="rounded-full border border-line px-2 py-0.5 text-xs text-ink-muted">
                                ({formatSessionAuditRole(t, event.actor_role)})
                              </span>
                              <span className="rounded-full border border-line px-2 py-0.5 text-xs text-ink-muted">
                                (
                                {formatSessionActionLabel(t, event.action_type)}
                                )
                              </span>
                              <span
                                className="max-w-[320px] truncate rounded-full border border-line px-2 py-0.5 text-xs text-ink-muted"
                                title={affectedEntity}
                              >
                                ({affectedEntity})
                              </span>
                            </p>
                            <button
                              type="button"
                              onClick={() =>
                                setExpandedSessionAuditEventIds((prev) =>
                                  prev.includes(event.id)
                                    ? prev.filter((item) => item !== event.id)
                                    : [...prev, event.id]
                                )
                              }
                              className="flex-shrink-0 rounded-lg border border-line px-2 py-1 text-xs font-medium text-gray-600 hover:bg-surface-hover dark:text-gray-300"
                            >
                              {isExpanded
                                ? t("sessionDetailPage.audit.collapse")
                                : t("sessionDetailPage.audit.expand")}
                            </button>
                          </div>

                          {isExpanded ? (
                            <div className="mt-2 border-t border-line pt-2">
                              {changedFieldRows.length === 0 ? (
                                <p className="text-xs text-ink-muted">
                                  {t("sessionDetailPage.audit.noFieldChanges")}
                                </p>
                              ) : (
                                <div className="space-y-1 text-xs text-gray-600 dark:text-gray-300">
                                  {changedFieldRows.map((row) => (
                                    <p key={`${event.id}-${row.key}`}>
                                      <span className="font-medium">
                                        {formatSessionFieldLabel(t, row.key)}:
                                      </span>{" "}
                                      <span className="inline-flex items-center rounded-full border border-line bg-surface-muted px-2 py-0.5 text-[11px] font-medium text-ink-muted">
                                        ({row.before})
                                      </span>{" "}
                                      <span>-&gt;</span>{" "}
                                      <span className="inline-flex items-center rounded-full border border-brand-200 bg-brand-50 px-2 py-0.5 text-[11px] font-medium text-brand-700 dark:border-brand-800/60 dark:bg-brand-900/20 dark:text-brand-300">
                                        ({row.after})
                                      </span>
                                    </p>
                                  ))}
                                </div>
                              )}
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )
          ) : null}
        </Card>
      ) : null}

      {showCaptureModal && sessionId && (
        <CaptureModal
          sessionId={sessionId}
          classId={id ?? ""}
          defaultAction="checkin"
          participants={
            isTrainer ? trainerSelfParticipants : attendanceParticipants
          }
          allowOutsideCheckin={Boolean(sessionData.allow_outside_checkin)}
          sessionLocation={sessionData.location}
          trainerOnly={isTrainer}
          onClose={() => setShowCaptureModal(false)}
          onSuccess={() => {
            reloadAttendance().catch(() => {});
            if (sessionId) {
              classMgmtService
                .getSessionParticipants(sessionId)
                .then(setParticipants)
                .catch(() => {});
            }
          }}
        />
      )}

      {showSelfCheckModal && sessionId && (
        <SelfCheckModal
          sessionId={sessionId}
          onClose={() => setShowSelfCheckModal(false)}
          onSuccess={() => {
            reloadAttendance().catch(() => {});
            if (sessionId) {
              classMgmtService
                .getSessionParticipants(sessionId)
                .then(setParticipants)
                .catch(() => {});
            }
          }}
        />
      )}
    </div>
  );
};

export default SessionDetail;
